import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { Link } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Leaderboard } from '@/components/stats/Leaderboard'
import { RecentGames } from '@/components/stats/RecentGames'
import { Gamepad2, Users, Trophy, TrendingUp, Plus } from 'lucide-react'
import { useState } from 'react'

export function Dashboard() {
  const { getAccessTokenSilently, user } = useAuth0()
  const [showCreateEnv, setShowCreateEnv] = useState(false)
  const [envName, setEnvName] = useState('Company Controlling')

  const { data: teams, isLoading, refetch } = useQuery({
    queryKey: ['teams', user?.sub],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get('/teams')
      return response.data
    },
    enabled: !!user,
  })

  const createTeam = useMutation({
    mutationFn: async (name: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/teams', { name })
      return response.data
    },
    onSuccess: () => {
      refetch()
      setShowCreateEnv(false)
    },
    onError: (error) => {
      console.error('Failed to create team:', error)
    },
  })

  const currentTeam = teams?.[0]

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', currentTeam?.id],
    queryFn: async () => {
      if (!currentTeam) return null
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get(`/stats/team/${currentTeam.id}`)
      return response.data
    },
    enabled: !!currentTeam,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
        <span className="ml-2">Loading teams...</span>
      </div>
    )
  }

  if (statsLoading && currentTeam) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
        <span className="ml-2">Loading statistics...</span>
      </div>
    )
  }

  // No team yet - show onboarding
  if (!currentTeam && !showCreateEnv) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome to FoosFi.re!</h1>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating an team or joining an existing one
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <Gamepad2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Create Team</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Start a new team for your colleagues.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateEnv(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Team
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Join Team</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Join an existing team with an invite code.
                </p>
                <div className="mt-6">
                  <Link
                    to="/join"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Join Team
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showCreateEnv) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Team</h1>
          <p className="mt-1 text-sm text-gray-500">
            Set up your FoosFi.re team
          </p>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="env-name" className="block text-sm font-medium text-gray-700">
                  Team Name
                </label>
                <input
                  type="text"
                  id="env-name"
                  value={envName}
                  onChange={(e) => setEnvName(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., Company Controlling"
                />
              </div>

              {createTeam.error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">
                    Error: {createTeam.error.message || 'Failed to create team'}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateEnv(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    console.log('Creating team with name:', envName)
                    createTeam.mutate(envName)
                  }}
                  disabled={!envName || createTeam.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {createTeam.isPending ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome to FoosFi.re
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Gamepad2 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Games
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.total_games || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Players
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.most_active_players?.length || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Trophy className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Top Player
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.leaderboard?.[0]?.display_name || '-'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Recent Games
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.recent_games?.length || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard */}
        <Leaderboard 
          players={stats?.leaderboard || []} 
          title="Leaderboard"
          maxItems={5}
        />

        {/* Recent Games */}
        <RecentGames 
          games={stats?.recent_games || []} 
          title="Recent Games"
          maxItems={5}
        />
      </div>
    </div>
  )
}
